import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useResponsiveContext } from '../context/ResponsiveContext'

/**
 * ViewportDebugger - Development component to debug viewport issues
 * Only renders in development mode with toggle functionality
 */
export default function ViewportDebugger() {
  const [isVisible, setIsVisible] = useState(false)
  const [viewportInfo, setViewportInfo] = useState({
    width: 0,
    height: 0,
    devicePixelRatio: 1,
    orientation: 'portrait',
    lastUpdate: new Date()
  })

  const responsive = useResponsiveContext()

  // Load visibility state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('viewport-debug-visible')
    if (savedState !== null) {
      setIsVisible(JSON.parse(savedState))
    }
  }, [])

  // Save visibility state to localStorage
  useEffect(() => {
    localStorage.setItem('viewport-debug-visible', JSON.stringify(isVisible))
  }, [isVisible])

  // Keyboard shortcut to toggle debugger (Ctrl/Cmd + Shift + D)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault()
        setIsVisible(!isVisible)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isVisible])

  useEffect(() => {
    const updateViewportInfo = () => {
      setViewportInfo({
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
        lastUpdate: new Date()
      })
    }

    updateViewportInfo()
    window.addEventListener('resize', updateViewportInfo)
    window.addEventListener('orientationchange', updateViewportInfo)

    return () => {
      window.removeEventListener('resize', updateViewportInfo)
      window.removeEventListener('orientationchange', updateViewportInfo)
    }
  }, [])

  if (process.env.NODE_ENV !== 'development') {
    return null
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
  }

  const debugPanelVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8, 
      y: -20,
      transition: { duration: 0.2 }
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  }

  const toggleButtonVariants = {
    idle: { scale: 1, rotate: 0 },
    hover: { scale: 1.1, rotate: 5 },
    pressed: { scale: 0.95 }
  }

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        onClick={toggleVisibility}
        className="fixed top-4 right-4 z-modal bg-black text-white p-2 rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 group"
        variants={toggleButtonVariants}
        initial="idle"
        whileHover="hover"
        whileTap="pressed"
        aria-label={isVisible ? "Hide viewport debugger" : "Show viewport debugger"}
        title={`${isVisible ? "Hide" : "Show"} viewport debugger (Ctrl/Cmd + Shift + D)`}
      >
        <div className="relative">
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={`transition-transform duration-200 ${isVisible ? 'rotate-180' : ''}`}
          >
            <path d="M3 3h18v18H3zM9 9h6v6H9z"/>
            <path d="M9 3v6M15 3v6M9 15v6M15 15v6M3 9h6M15 9h6M3 15h6M15 15h6"/>
          </svg>
          {/* Active indicator */}
          {isVisible && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-black animate-pulse" />
          )}
        </div>
      </motion.button>

      {/* Debug Panel */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="fixed top-16 right-4 z-modal bg-black text-white p-4 rounded-lg text-xs font-mono max-w-xs shadow-2xl border border-white/20"
            variants={debugPanelVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <div className="font-bold mb-3 text-green-400 flex items-center justify-between">
              <span>Viewport Debug</span>
              <button
                onClick={toggleVisibility}
                className="text-white/60 hover:text-white transition-colors duration-200"
                aria-label="Close debugger"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Viewport:</span>
                <span className="text-green-300">{viewportInfo.width}×{viewportInfo.height}</span>
              </div>
              <div className="flex justify-between">
                <span>DPR:</span>
                <span className="text-blue-300">{viewportInfo.devicePixelRatio}</span>
              </div>
              <div className="flex justify-between">
                <span>Orientation:</span>
                <span className="text-purple-300">{viewportInfo.orientation}</span>
              </div>
              <div className="flex justify-between">
                <span>Updated:</span>
                <span className="text-gray-300 text-xs">
                  {viewportInfo.lastUpdate.toLocaleTimeString()}
                </span>
              </div>
              
              <div className="border-t border-white/20 pt-2 mt-2">
                <div className="font-semibold text-blue-400 mb-1">Responsive State:</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex justify-between">
                    <span>Mobile:</span>
                    <span className={responsive.isMobile ? 'text-green-400' : 'text-red-400'}>
                      {responsive.isMobile ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tablet:</span>
                    <span className={responsive.isTablet ? 'text-green-400' : 'text-red-400'}>
                      {responsive.isTablet ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Desktop:</span>
                    <span className={responsive.isDesktop ? 'text-green-400' : 'text-red-400'}>
                      {responsive.isDesktop ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Touch:</span>
                    <span className={responsive.isTouch ? 'text-green-400' : 'text-red-400'}>
                      {responsive.isTouch ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hover:</span>
                    <span className={responsive.hasHover ? 'text-green-400' : 'text-red-400'}>
                      {responsive.hasHover ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-2 mt-2">
                <div className="font-semibold text-yellow-400 mb-1">Layout:</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex justify-between">
                    <span>Bottom Nav:</span>
                    <span className={responsive.showBottomNav ? 'text-green-400' : 'text-red-400'}>
                      {responsive.showBottomNav ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Side Drawer:</span>
                    <span className={responsive.showSideDrawer ? 'text-green-400' : 'text-red-400'}>
                      {responsive.showSideDrawer ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sidebar:</span>
                    <span className={responsive.showSidebar ? 'text-green-400' : 'text-red-400'}>
                      {responsive.showSidebar ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/20 pt-2 mt-2">
                <div className="font-semibold text-purple-400 mb-1">Breakpoints:</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="flex justify-between">
                    <span>sm:</span>
                    <span className={responsive.sm ? 'text-green-400' : 'text-red-400'}>
                      {responsive.sm ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>md:</span>
                    <span className={responsive.md ? 'text-green-400' : 'text-red-400'}>
                      {responsive.md ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>lg:</span>
                    <span className={responsive.lg ? 'text-green-400' : 'text-red-400'}>
                      {responsive.lg ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>xl:</span>
                    <span className={responsive.xl ? 'text-green-400' : 'text-red-400'}>
                      {responsive.xl ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>2xl:</span>
                    <span className={responsive['2xl'] ? 'text-green-400' : 'text-red-400'}>
                      {responsive['2xl'] ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
